import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';

import RefreshIcon from '@mui/icons-material/Refresh';
import { Button, FormControl, IconButton, MenuItem, Select, Stack, Switch, TextField } from "@mui/material";

import { api } from '../../../../../utils';
import { IError } from '../../../../../models/ReportPageModels';
import { XeroAccountModel, XeroProductModel } from '../CreateBillingProduct/CreateBillingProduct';

interface AdminGetXeroProductsResponse {
    data: XeroProductsData;
    error: IError;
}
interface XeroProductsData {
    products: XeroProductModel[];
}

interface AdminGetBillingProductsResponse {
    data: BillingProductsData;
    error: IError;
}
interface BillingProductsData {
    products: BillingProductModel[];
}

interface AdminGetXeroAccountsResponse {
    data: XeroAccountsData;
    error: IError;
}
interface XeroAccountsData {
    accounts: XeroAccountModel[];
}
interface BillingProductModel {
    id: number;
    productCode: string;
    description: string;
    databaseLookup: number;
    boothIdFilter: number;
    billingPer: string;
    unitPrice: number;
    taxRate: number;
    allowFree: number;
    allowCommission: number;
    allowHide: number;
    xeroProduct: string;
    xeroAccount: string;
}

interface EditBillingProductResponse {
    data: EditBillingProductData;
    error: IError;
}
interface EditBillingProductData {
    status: string;
}

const BillingPerOptions: string[] = ["/month", "/vehicle", "/image"];

function EditBillingProduct() {
    const { enqueueSnackbar } = useSnackbar();

    const [productId, setProductId] = useState<number>(0);

    const [productCode, setProductCode] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [dbLookup, setDbLookup] = useState<boolean>(false);
    const [boothIdFilter, setBoothIdFilter] = useState<boolean>(false);
    const [billingType, setBillingType] = useState<string>("/month");
    const [unitPrice, setUnitPrice] = useState<number>(0.0);
    const [taxRate, setTaxRate] = useState<boolean>(true);
    const [allowFree, setAllowFree] = useState<boolean>(false);
    const [allowCommission, setAllowCommission] = useState<boolean>(false);
    const [allowHide, setAllowHide] = useState<boolean>(true);

    const [selectedXeroProduct, setSelectedXeroProduct] = useState<string>("");
    const [selectedXeroAccount, setSelectedXeroAccount] = useState<string>("");

    const [billingProducts, setBillingProducts] = useState<BillingProductModel[]>([]);
    const [xeroProducts, setXeroProducts] = useState<XeroProductModel[]>([]);
    const [xeroAccounts, setXeroAccounts] = useState<XeroAccountModel[]>([]);

    useEffect(() => {
        GetBillingProducts();
        GetXeroProducts();
        GetXeroAccounts();
    }, []);

    const GetBillingProducts = async () => {
        try {
            const response: AdminGetBillingProductsResponse = await api.get("api/Admin/GetBillingProductsDb", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setBillingProducts(response.data.products);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const GetXeroProducts = async () => {
        try {
            const response: AdminGetXeroProductsResponse = await api.get("api/Xero/GetProducts", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setXeroProducts(response.data.products)
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const GetXeroAccounts = async () => {
        try {
            const response: AdminGetXeroAccountsResponse = await api.get("api/Xero/GetAccounts", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setXeroAccounts(response.data.accounts);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function SetProductId(event: any) {
        var value: number = event.target.value;
        setProductId(value);
        var product = null;

        for (var i = 0; i < billingProducts.length; i++) {
            if (billingProducts[i].id === value) {
                product = billingProducts[i]
                break;
            }
        }

        if (product === null)
            return;

        setProductCode(product.productCode);
        setDescription(product.description);
        setDbLookup(product.databaseLookup === 1);
        setBoothIdFilter(product.boothIdFilter === 1);
        setBillingType(product.billingPer);
        setUnitPrice(product.unitPrice);
        setTaxRate(product.taxRate === 1);
        setAllowFree(product.allowFree === 1);
        setAllowCommission(product.allowCommission === 1);
        setAllowHide(product.allowHide === 1);
        setSelectedXeroProduct(product.xeroProduct);
        setSelectedXeroAccount(product.xeroAccount);
    }

    const EditProduct = async () => {
        if (productId === 0 || productCode === "" || selectedXeroProduct === "") {
            enqueueSnackbar("Please recheck fields", { variant: "error" });
            return;
        }

        var body: BillingProductModel = {
            id: productId,
            productCode: productCode,
            description: description,
            databaseLookup: dbLookup ? 1 : 0,
            boothIdFilter: boothIdFilter ? 1 : 0,
            billingPer: billingType,
            unitPrice: unitPrice,
            taxRate: taxRate ? 1 : 0,
            allowFree: allowFree ? 1 : 0,
            allowCommission: allowCommission ? 1 : 0,
            allowHide: allowHide ? 1 : 0,
            xeroProduct: selectedXeroProduct,
            xeroAccount: selectedXeroAccount
        };

        try {
            const response: EditBillingProductResponse = await api.post("api/Admin/EditBillingProduct", {
                json: body,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                enqueueSnackbar("Edited!", { variant: "success" })
                // clear fields
                setProductId(0);
                setProductCode("");
                setDescription("");
                setDbLookup(false);
                setBoothIdFilter(false);
                setBillingType("/month");
                setUnitPrice(0.0);
                setTaxRate(true);
                setAllowFree(false);
                setAllowCommission(false);
                setAllowHide(true);
                setSelectedXeroProduct("");
                setSelectedXeroAccount("");

                // get new products from db
                GetBillingProducts();
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    return (
        <Stack>
            {/* Products */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Products: </p>
                <FormControl sx={{ minWidth: 245 }}>
                    <Select
                        value={productId}
                        onChange={SetProductId}
                        size="small"
                        defaultValue={-1}>
                        <MenuItem
                            value={0}>
                            {"Select product"}
                        </MenuItem>
                        {billingProducts.map((product, index) =>
                            <MenuItem
                                key={index}
                                value={product.id}>
                                {product.productCode}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
            </Stack>

            {/* ProductCode */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>ProductCode: </p>
                <TextField
                    value={productCode}
                    onChange={(newValue) => { setProductCode(newValue.target.value) }}
                    sx={{ minWidth: "245px" }}
                    size="small" />
            </Stack>

            {/* Description */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Description: </p>
                <TextField
                    value={description}
                    onChange={(newValue) => { setDescription(newValue.target.value) }}
                    sx={{ minWidth: "245px" }}
                    size="small" />
            </Stack>

            {/* Xero Products */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Xero Product: </p>
                <FormControl sx={{ minWidth: "245px" }}>
                    <Select
                        value={selectedXeroProduct}
                        onChange={(newValue: any) => { setSelectedXeroProduct(newValue.target.value); }}
                        size="small">
                        {xeroProducts.map((item, index) =>
                            <MenuItem
                                key={index}
                                value={item.id}>
                                {item.code} — {item.name}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
                <IconButton
                    color="primary"
                    size="large"
                    onClick={() => { GetXeroProducts() }}>
                    <RefreshIcon sx={{ fontSize: 26 }} />
                </IconButton>
            </Stack>

            {/* Xero Account */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Xero Account: </p>
                <FormControl sx={{ minWidth: "245px" }}>
                    <Select
                        value={selectedXeroAccount}
                        onChange={(newValue: any) => { setSelectedXeroAccount(newValue.target.value); }}
                        size="small">
                        {xeroAccounts.map((item, index) =>
                            <MenuItem
                                key={index}
                                value={item.name}>
                                {item.code} - {item.name}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
                <IconButton
                    color="primary"
                    size="large"
                    onClick={() => { GetXeroAccounts() }}>
                    <RefreshIcon sx={{ fontSize: 26 }} />
                </IconButton>
            </Stack>

            {/* Database lookup */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Database lookup: </p>
                <Switch
                    checked={dbLookup}
                    onChange={(newValue) => { setDbLookup(newValue.target.checked) }}
                    size="small" />
            </Stack>

            {/* BoothId Filter */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>BoothId Filter: </p>
                <Switch
                    checked={boothIdFilter}
                    onChange={(newValue) => { setBoothIdFilter(newValue.target.checked) }}
                    size="small" />
            </Stack>

            {/* Unit Price */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Unit Price: </p>
                <TextField
                    value={unitPrice}
                    onChange={(newValue: any) => { setUnitPrice(newValue.target.value) }}
                    sx={{ minWidth: "245px" }}
                    size="small" />
            </Stack>

            {/* Billing per */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Billing type: </p>
                <FormControl sx={{ minWidth: "245px" }}>
                    <Select
                        value={billingType}
                        onChange={(newValue: any) => { setBillingType(newValue.target.value); }}
                        size="small">
                        {BillingPerOptions.map((item, index) =>
                            <MenuItem
                                key={index}
                                value={item}>
                                {item}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
            </Stack>

            {/* Tax Rate */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Tax Rate: </p>
                <Switch
                    checked={taxRate}
                    onChange={(newValue) => { setTaxRate(newValue.target.checked) }}
                    size="small" />
            </Stack>

            {/* Allow Free */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Allow Free: </p>
                <Switch
                    checked={allowFree}
                    onChange={(newValue) => { setAllowFree(newValue.target.checked) }}
                    size="small" />
            </Stack>

            {/* Allow Commission */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Allow Commission: </p>
                <Switch
                    checked={allowCommission}
                    onChange={(newValue) => { setAllowCommission(newValue.target.checked) }}
                    size="small" />
            </Stack>

            {/* Allow Hide */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Allow Hide: </p>
                <Switch
                    checked={allowHide}
                    onChange={(newValue) => { setAllowHide(newValue.target.checked) }}
                    size="small" />
            </Stack>

            <Button
                variant="outlined"
                onClick={() => EditProduct()}
                sx={{ width: '82px' }}>
                Edit
            </Button>
        </Stack>
    )
}

export default EditBillingProduct;